import { createScopedLogger, formatString } from "@@common";
import { IExpressionEvaluatorContext, IVariableScope, ObjectVariableScope, VariableScopeObserver } from "@@expressions";
import { useEffect, useState } from "react";
import { IsDesignMode } from "../Utils/runtimeDetection";

export type ObservedTextResult = {
  text: string | undefined;
  observedPaths: string[];
};

export function useObservedTextResult(textTemplate: string | undefined, context: IExpressionEvaluatorContext, loggerName: string): ObservedTextResult {
  const logger = createScopedLogger(loggerName);

  // In runtime mode, short-circuit when no interpolation
  // In design mode, always run hooks because text could change dynamically to include variables
  if (!IsDesignMode && (!textTemplate || !textTemplate.includes("{"))) {
    return { text: textTemplate, observedPaths: [] };
  }

  // Note: These hooks are placed in this condition on purpoise to avoid unnecessary evaluation
  // The component definition WILL NOT change during the lifecycle of the component, so it's safe to call hooks conditionally based on its properties

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [text, setText] = useState("");
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [observedPaths, setObservedPaths] = useState<string[]>([]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    // If no template or no interpolation, just set the text as-is
    if (!textTemplate || !textTemplate.includes("{")) {
      setText(textTemplate ?? "");
      setObservedPaths([]);
      return;
    }

    const observedPaths: string[] = [];
    const initialText = formatString(textTemplate, context.VariableScope, observedPaths);
    logger.debug("Observed paths for text template:", observedPaths);
    setText(initialText);
    setObservedPaths(observedPaths);

    if (observedPaths.length === 0) {
      // No dynamic paths to observe, nothing to do
      return;
    }

    const handleVariableScopeChanged: VariableScopeObserver = (target: IVariableScope, name: string | null, value: any, prevValue: any) => {
      if (
        name !== null &&
        !observedPaths.some((observedPath) => {
          const lowerObserved = observedPath.toLowerCase();
          const lowerChanged = name.toLowerCase();

          // Exact match
          if (lowerObserved === lowerChanged) return true;

          // Check if observed path is a child of changed path (e.g., observing $item.contact, $item changed)
          // The observed path should start with changed path followed by a dot
          if (lowerObserved.startsWith(lowerChanged + ".")) return true;

          // Check if changed path is a child of observed path (e.g., observing $item, $item.contact changed)
          // The changed path should start with observed path followed by a dot
          if (lowerChanged.startsWith(lowerObserved + ".")) return true;

          return false;
        })
      ) {
        return;
      }
      // textTemplate is guaranteed to exist here because we're inside the interpolation branch
      const newText = formatString(textTemplate!, context.VariableScope);
      logger.debug("Variable changed, updating text:", name, newText);
      setText(newText);
    };

    // Register observer to update text on variable scope changes
    const objectVariableScope = context.VariableScope as ObjectVariableScope;
    logger.debug("Adding observer to variable scope.");
    objectVariableScope.addObserver(handleVariableScopeChanged);

    return () => {
      logger.debug("Removing observer from variable scope.");
      objectVariableScope.removeObserver(handleVariableScopeChanged);
    };
    // context.Id intentionally NOT in deps - scope replacement is detected by context.VariableScope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.VariableScope, textTemplate, loggerName]);

  return { text, observedPaths };
}

export default function useObservedText(textTemplate: string | undefined, context: IExpressionEvaluatorContext, loggerName: string): string | undefined {
  return useObservedTextResult(textTemplate, context, loggerName).text;
}
